import { apiClient } from '@/lib/apiClient';

/**
 * funnelObservabilityApi — ADR-022 Session 5.
 *
 * Read + triage operations for the composable AI funnel observability surface.
 * All endpoints under /api/ai are protected by onlyAdmin (admin-back).
 */

export type CoverageGapStatus =
  | 'open'
  | 'triaged'
  | 'taxonomy_extension_proposed'
  | 'resolved'
  | 'dismissed';

export interface CoverageGapTriage {
  adminUserId: string;
  notes?: string;
  proposedNewApproachId?: string;
  proposedNewArchetypeId?: string;
  proposedNewCategoryKey?: string;
  clusterId?: string;
}

export interface CoverageGap {
  gapId: string;
  userId: string;
  goalId: string;
  goalText: string;
  category: string;
  candidateApproaches: string[];
  composerReason: string;
  freeCompositionUsed: boolean;
  renderedObjective?: string;
  coherenceScore?: number;
  status: CoverageGapStatus;
  createdAt: string;
  triagedAt?: string;
  resolvedAt?: string;
  triage?: CoverageGapTriage;
}

export interface CoverageGapListPage {
  gaps: CoverageGap[];
  nextCursor: string | null;
}

export interface ListCoverageGapsParams {
  /** CSV ('open,triaged') or single status. Empty/undefined returns all. */
  status?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  cursor?: string;
  limit?: number;
}

export interface TriageCoverageGapBody {
  status: 'triaged' | 'taxonomy_extension_proposed' | 'resolved' | 'dismissed';
  notes?: string;
  proposedNewApproachId?: string;
  proposedNewArchetypeId?: string;
  proposedNewCategoryKey?: string;
  clusterId?: string;
}

export interface ApproachOutcomeCell {
  cellKey: string;
  categoryKey: string;
  archetypeId: string;
  approachId: string;
  adaptationHash: string;
  selections: number;
  completions: number;
  abandonments: number;
  abandonmentWeekHistogram?: number[];
  satisfactionSum?: number;
  satisfactionCount?: number;
  weightDecay?: number;
  lastUpdated: string;
  completionRate: number;
  abandonmentRate: number;
}

export interface ApproachOutcomeCellPage {
  category: string;
  cells: ApproachOutcomeCell[];
}

export interface ListApproachOutcomeCellsParams {
  minSelections?: number;
  limit?: number;
}

export type PortfolioHealth = 'healthy' | 'at_risk' | 'overloaded';

export interface ActivePhaseEntry {
  goalId: string;
  phaseId: string;
  categoryKey: string;
  approachId: string;
  commitmentLevel: 'low' | 'moderate' | 'high';
  weeklyLoadScore: number;
  startDate: string;
  plannedEndDate: string;
  daysRemaining: number;
  completionRatioSoFar: number;
}

export interface RecentOutcome {
  goalId: string;
  phaseId: string;
  event: 'promoted' | 'retry' | 'failed' | 'abandoned';
  at: string;
}

export interface UserPortfolioSnapshot {
  userId: string;
  activePhases: ActivePhaseEntry[];
  totalWeeklyLoad: number;
  loadCeiling: number;
  recentOutcomes7d: RecentOutcome[];
  portfolioHealth: PortfolioHealth;
  stateVersion: number;
  lastReconciledAt: string;
  updatedAt: string;
}

export interface UniversalAiModelView {
  voiceRegister?: string;
  voiceConfidence?: number;
  lifeConstraints?: {
    timeWindow?: unknown[];
    obligations?: string[];
    contraindications?: string[];
  };
  constraintsConfidence?: Record<string, number>;
  baselineSelfEfficacy?: 'low' | 'moderate' | 'high';
  abandonmentRisk?: string[];
  modelVersion?: number;
  updatedAt?: string;
  lastRefreshReason?: string;
}

export interface JourneyModelView {
  identityClaim?: string;
  statedGoal?: string;
  underlyingNeed?: string | null;
  readinessStage?: string;
  archetype?: string;
  domainSelfEfficacy?: 'low' | 'moderate' | 'high';
  goalSpecificConstraints?: string[];
  phaseFramingDefault?: string;
  fieldConfidence?: Record<string, number>;
  modelVersion?: number;
  updatedAt?: string;
  lastRefreshReason?: string;
}

export interface AiUserModelsResponse {
  userId: string;
  universalAiModel: UniversalAiModelView | null;
  journeyModels: Array<{
    goalId: string;
    title: string;
    category?: string;
    isDeleted: boolean;
    journeyModel: JourneyModelView | null;
  }>;
}

export const funnelObservabilityApi = {
  listCoverageGaps: (params: ListCoverageGapsParams = {}) =>
    apiClient.get<CoverageGapListPage>('/ai/coverage-gaps', { ...params }),
  getCoverageGap: (gapId: string) =>
    apiClient.get<CoverageGap>(`/ai/coverage-gaps/${encodeURIComponent(gapId)}`),
  triageCoverageGap: (gapId: string, body: TriageCoverageGapBody) =>
    apiClient.post<CoverageGap>(
      `/ai/coverage-gaps/${encodeURIComponent(gapId)}/triage`,
      body,
    ),
  listApproachOutcomeCells: (
    categoryKey: string,
    params: ListApproachOutcomeCellsParams = {},
  ) =>
    apiClient.get<ApproachOutcomeCellPage>(
      `/ai/approach-outcomes/${encodeURIComponent(categoryKey)}`,
      { ...params },
    ),
  getApproachOutcomeCell: (categoryKey: string, cellKey: string) =>
    apiClient.get<ApproachOutcomeCell>(
      `/ai/approach-outcomes/${encodeURIComponent(categoryKey)}/cells/${encodeURIComponent(cellKey)}`,
    ),
  getUserPortfolio: (userId: string) =>
    apiClient.get<UserPortfolioSnapshot>(
      `/ai/portfolio-state/${encodeURIComponent(userId)}`,
    ),
  getUserModels: (userId: string) =>
    apiClient.get<AiUserModelsResponse>(
      `/ai/user-models/${encodeURIComponent(userId)}`,
    ),
};
